import { Suspense, lazy, useMemo } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';

import OrdersCleanedReport from '../../pages/Reports/OrdersCleanedReport';
import DailySalesReport from '../../pages/Reports/DailySalesReport';
import MonthlySalesReport from '../../pages/Reports/MonthlySalesReport';
import CustomersReport from '../../pages/Reports/CustomersReport';
import CashupReportsReport from '../../pages/Reports/CashupReportsReport';
import SuppliesReport from '../../pages/Reports/SuppliesReport';
import InventoryStockReport from '../../pages/Reports/InventoryStockReport';
import SupplyStockReport from '../../pages/Reports/SupplyStockReport';
import StaffReport from '../../pages/Reports/StaffReport';
import DiscountCodesReport from '../../pages/Reports/DiscountCodesReport';
import SalesProductReport from '../../pages/Reports/ProductSalesReport';
import PaymentLinesReport from '../../pages/Reports/PaymentLinesReport';
import CashierBoxMovementsReport from '../../pages/Reports/CashierBoxMovementsReport';
import UnpaidOrdersReport from '../../pages/Reports/UnpaidOrdersReport';
import GeneralViewReport from '../../pages/Reports/GeneralViewReport';
import AttendanceReport from '../../pages/Reports/AttendanceReport';
import ScheduledDeliveriesReport from '../../pages/Reports/ScheduledDeliveriesReport';
import ServicesToDeliverReport from '../../pages/Reports/ServicesToDeliverReport';
import OutsourcingReport from '../../pages/Reports/OutsourcingReport';
import OrdersHistoryReport from '../../pages/Reports/OrdersHistoryReport';
import FinancialSummaryReport from '../../pages/Reports/FinancialSummaryReport';
import { useSelector } from 'react-redux';
import PageNotFound from '../../pages/PageNotFound';
import ChunkErrorBoundary from '../ChunkErrorBoundary';
import OutsourcingOrdersList from '../../pages/Outsourcing/List';
import PublicReceipt from '../../pages/PublicReceipt';
import LoaderOverlay from '../LoaderOverlay/LoaderOverlay';
import { CompanyPlanStatus } from '../../enum/CompanyPlanLevelEnum';
import { hasPermission, type PermissionAction } from '../../utils/permission-utils';

// MANTINE ROUTES
const WorkBoard = lazy(() => import('../../pages/WorkBoard'));
const Staffs = lazy(() => import('../../pages/Staff/List'));
const StaffCreatePage = lazy(() => import('../../pages/Staff/Create'));
const StaffEditPage = lazy(() => import('../../pages/Staff/Edit'));
const Stores = lazy(() => import('../../pages/Stores/List'));
const StoreEditPage = lazy(() => import('../../pages/Stores/Edit'));
const StoreCreatePage = lazy(() => import('../../pages/Stores/Create'));
const DiscountCodes = lazy(() => import('../../pages/Discounts/DiscountCodes/List'));
const DiscountCodeCreatePage = lazy(() => import('../../pages/Discounts/DiscountCodes/Create'));
const DiscountCodeEditPage = lazy(() => import('../../pages/Discounts/DiscountCodes/Edit'));
const AutomaticDiscounts = lazy(() => import('../../pages/Discounts/AutomaticDiscounts/List'));
const AutomaticDiscountCreatePage = lazy(
  () => import('../../pages/Discounts/AutomaticDiscounts/Create')
);
const AutomaticDiscountEditPage = lazy(
  () => import('../../pages/Discounts/AutomaticDiscounts/Edit')
);
const CFDITermsAndConditions = lazy(
  () => import('../../pages/CFDITermsAndConditions/CFDITermsAndConditions')
);
const SettingsLayout = lazy(() => import('../../components/SettingsLayout/SettingsLayout'));
const CustomerReviews = lazy(() => import('../../pages/CustomerReview/CustomerReview'));
const CashierBoxes = lazy(() => import('../../pages/CashierBoxes/List'));
const CashierBoxCreatePage = lazy(() => import('../../pages/CashierBoxes/Create'));
const CashierBoxEditPage = lazy(() => import('../../pages/CashierBoxes/Edit'));
const CashierBoxAddMovementPage = lazy(() => import('../../pages/CashierBoxes/AddMovement'));
const CashierBoxesActivity = lazy(() => import('../../pages/CashierBoxes/CashierboxHistory'));
const Expenses = lazy(() => import('../../pages/Expenses/List'));
const ExpenseCreatePage = lazy(() => import('../../pages/Expenses/Create'));
const ExpenseEditPage = lazy(() => import('../../pages/Expenses/Edit'));
const Supplies = lazy(() => import('../../pages/Supplies/List'));
const SupplyEditPage = lazy(() => import('../../pages/Supplies/Edit'));
const SupplyCreatePage = lazy(() => import('../../pages/Supplies/Create'));
const SupplyHistory = lazy(() => import('../../pages/Supplies/HistoryList'));
const SupplyAddStockPage = lazy(() => import('../../pages/Supplies/AddStock'));
const Inventory = lazy(() => import('../../pages/Inventory/List'));
const InventoryCreatePage = lazy(() => import('../../pages/Inventory/Create'));
const InventoryEditPage = lazy(() => import('../../pages/Inventory/Edit'));
const InventoryHistory = lazy(() => import('../../pages/Inventory/HistoryList'));
const InventoryAddStockPage = lazy(() => import('../../pages/Inventory/AddStock'));
const Sections = lazy(() => import('../../pages/Sections/List'));
const SectionsEditPage = lazy(() => import('../../pages/Sections/Edit'));
const SectionsCreatePage = lazy(() => import('../../pages/Sections/Create'));
const Products = lazy(() => import('../../pages/Products/List'));
const ProductsCreatePage = lazy(() => import('../../pages/Products/Create'));
const ProductsEditPage = lazy(() => import('../../pages/Products/Edit'));
const Routes = lazy(() => import('../../pages/Routes/List'));
const RoutesCreatePage = lazy(() => import('../../pages/Routes/Create'));
const RoutesEditPage = lazy(() => import('../../pages/Routes/Edit'));
const Customers = lazy(() => import('../../pages/Customers/List'));
const CustomerCreatePage = lazy(() => import('../../pages/Customers/Create'));
const CustomerEditPage = lazy(() => import('../../pages/Customers/Edit'));
const CustomerOrders = lazy(() => import('../../pages/Customers/HistoryList'));
const CustomerLoyaltyHistory = lazy(() => import('../../pages/Customers/LoyaltyHistoryList'));
const Reviews = lazy(() => import('../../pages/Reviews/List'));
const Subscribe = lazy(() => import('../../pages/Subscribe/Subscribe'));
const GetMoreOrders = lazy(() => import('../../pages/GetMore/GetMore'));
const Invoices = lazy(() => import('../../pages/CFDI/List'));
const CFDIOrdersList = lazy(() => import('../../pages/CFDI/CFDIOrdersList'));
const CFDIPreviewPage = lazy(() => import('../../pages/CFDI/CFDIPreviewPage'));

// páginas de operación diaria — imports estáticos
import NewOrderNewExperience from '../../pages/NewOrder';
import Orders from '../../pages/Orders/List';
import Cleaning from '../../pages/Cleaning/List';
import Ready from '../../pages/Ready/List';
import Pickup from '../../pages/PickupAndDelivery/List';
import RequesedOrders from '../../pages/RequestedOrders/List';
import Profile from '../../pages/Profile/Edit';
import OrderPrintHalfLetter from '../../pages/OrderPrintHalfLetter';

// CashUp (corte de caja) — imports estáticos
import CashUp from '../../pages/CashUps/List';
import CashUpCreatePage from '../../pages/CashUps/Create';
import CashUpEditPage from '../../pages/CashUps/CashupReport';
import TermsAndConditions from '../../pages/TermsAndConditions/TermsAndConditions';

export function AppLayoutContentRoutes() {
  const { user, company } = useSelector((state) => state.user);
  const hasCompanyCFDIService = !!company?.companyServicesPermissions?.canUseCFDIInvoicing;
  const isInGraceOrSuspended = [CompanyPlanStatus.GRACE, CompanyPlanStatus.SUSPENDED].includes(
    company?.planStatus
  );
  const canUseCFDIByCompany = hasCompanyCFDIService && !isInGraceOrSuspended;
  // Rollout-safe fallback: treat missing permission.cfdi as allowed until migration/client
  // updates converge, then explicit flags take over.
  const canViewCFDIRoutes = canUseCFDIByCompany && (user?.permission?.cfdi?.canView ?? true);
  const canMutateCFDIRoutes =
    canUseCFDIByCompany && (user?.permission?.cfdi?.canMutate ?? true);

  const cachedRoutes = useMemo(() => {
    const routes: any = [];
    const addPagesRoutes = (
      pageType: string,
      validateViewPages: any[],
      validateEditPages: any[],
      options?: {
        viewAction?: PermissionAction;
        viewFallbackAction?: PermissionAction;
        mutationAction?: PermissionAction;
      }
    ) => {
      const canViewPages = hasPermission(user, pageType, options?.viewAction ?? 'canView');
      const canUseViewFallback = options?.viewFallbackAction
        ? hasPermission(user, pageType, options.viewFallbackAction)
        : false;
      if (canViewPages || canUseViewFallback) {
        routes.push(...validateViewPages);
      }
      if (hasPermission(user, pageType, options?.mutationAction ?? 'canMutate')) {
        routes.push(...validateEditPages);
      }
    };

    //THIS WILL ADD THE ROUTES IN CASE IT HAS PERMISSIONS
    addPagesRoutes(
      'store',
      [<PrivateRoute path="/stores" component={Stores} exact />],
      [
        <PrivateRoute path="/stores/create" component={StoreCreatePage} exact />,
        <PrivateRoute path="/stores/:id" component={StoreEditPage} />
      ]
    );
    addPagesRoutes(
      'product',
      [<PrivateRoute path="/products" component={Products} exact />],
      [
        <PrivateRoute path="/products/create" component={ProductsCreatePage} exact />,
        <PrivateRoute path="/products/:id" component={ProductsEditPage} />
      ]
    );
    addPagesRoutes(
      'section',
      [<PrivateRoute path="/sections" component={Sections} exact />],
      [
        <PrivateRoute path="/sections/create" component={SectionsCreatePage} exact />,
        <PrivateRoute path="/sections/:id" component={SectionsEditPage} />
      ]
    );
    addPagesRoutes(
      'staff',
      [<PrivateRoute path="/staff" component={Staffs} exact />],
      [
        <PrivateRoute path="/staff/create" component={StaffCreatePage} exact />,
        <PrivateRoute path="/staff/:id" component={StaffEditPage} />
      ]
    );
    addPagesRoutes(
      'settings',
      [
        <PrivateRoute path="/settings/:menu" component={SettingsLayout} exact />,
        <PrivateRoute path="/settings" component={SettingsLayout} exact />
      ],
      []
    );
    addPagesRoutes(
      'cashierBoxes',
      [
        <PrivateRoute path="/cashierBoxes" component={CashierBoxes} exact />,
        <PrivateRoute path="/cashierBoxes/:id/history" component={CashierBoxesActivity} exact />
      ],
      [
        <PrivateRoute path="/cashierBoxes/create" component={CashierBoxCreatePage} exact />,
        <PrivateRoute path="/cashierBoxes/:id" component={CashierBoxEditPage} exact />,
        <PrivateRoute
          path="/cashierBoxes/:id/movement"
          component={CashierBoxAddMovementPage}
          exact
        />
      ],
      { viewFallbackAction: 'canMutate' }
    );
    addPagesRoutes(
      'expenses',
      [],
      [<PrivateRoute path="/expenses/create" component={ExpenseCreatePage} exact />],
      { mutationAction: 'canCreate' }
    );
    addPagesRoutes(
      'expenses',
      [
        <PrivateRoute path="/expenses" component={Expenses} exact />,
        <PrivateRoute path="/expenses/:id" component={ExpenseEditPage} exact />
      ],
      [],
      { viewAction: 'canView' }
    );
    addPagesRoutes(
      'reports',
      [
        <PrivateRoute exact path={`/reports`} component={GeneralViewReport} />,
        <PrivateRoute exact path={`/reports/cleaned-report`} component={OrdersCleanedReport} />,
        <PrivateRoute exact path={`/reports/sales-report`} component={DailySalesReport} />,
        <PrivateRoute
          exact
          path={`/reports/sales-report-monthly`}
          component={MonthlySalesReport}
        />,
        <PrivateRoute exact path={`/reports/customers-report`} component={CustomersReport} />,
        <PrivateRoute exact path={`/reports/cashup-reports-report`} component={CashupReportsReport} />,
        <PrivateRoute exact path={`/reports/supplies-report`} component={SuppliesReport} />,
        ...(hasPermission(user, 'inventory', 'canView')
          ? [
              <PrivateRoute key="inventory-stock" exact path="/reports/inventory-stock" component={InventoryStockReport} />,
              <PrivateRoute key="supply-stock" exact path="/reports/supply-stock" component={SupplyStockReport} />
            ]
          : []),
        <PrivateRoute
          exact
          path={`/reports/unpaid-orders-report`}
          component={UnpaidOrdersReport}
        />,
        <PrivateRoute
          exact
          path={`/reports/discountCodes-report`}
          component={DiscountCodesReport}
        />,
        <PrivateRoute exact path={`/reports/staff-report`} component={StaffReport} />,
        <PrivateRoute
          exact
          path={`/reports/product-sales-report`}
          component={SalesProductReport}
        />,
        <PrivateRoute
          exact
          path={`/reports/payment-lines-report`}
          component={PaymentLinesReport}
        />,
        <PrivateRoute
          exact
          path={`/reports/cashierbox-movements-report`}
          component={CashierBoxMovementsReport}
        />,
        <PrivateRoute exact path={`/reports/attendance-report`} component={AttendanceReport} />,
        <PrivateRoute exact path={`/reports/scheduled-deliveries-report`} component={ScheduledDeliveriesReport} />,
        <PrivateRoute exact path={`/reports/orders-history-report`} component={OrdersHistoryReport} />,
        <PrivateRoute exact path={`/reports/services-to-deliver-report`} component={ServicesToDeliverReport} />,
        <PrivateRoute exact path={`/reports/outsourcing-report`} component={OutsourcingReport} />
      ],
      []
    );
    addPagesRoutes(
      'customers',
      [
        <PrivateRoute path="/customers" component={Customers} exact />,
        <PrivateRoute path="/customers/:id/orders" component={CustomerOrders} exact />,
        <PrivateRoute
          path="/customers/:id/loyalty-history"
          component={CustomerLoyaltyHistory}
          exact
        />
      ],
      [<PrivateRoute path="/customers/create" component={CustomerCreatePage} exact />],
      { mutationAction: 'canCreate' }
    );
    addPagesRoutes(
      'customers',
      [],
      [<PrivateRoute path="/customers/:id" component={CustomerEditPage} exact />],
      { mutationAction: 'canEdit' }
    );
    addPagesRoutes(
      'discounts',
      [
        <PrivateRoute path="/discounts/discountCodes" component={DiscountCodes} exact />,
        <PrivateRoute path="/discounts/automaticCodes" component={AutomaticDiscounts} exact />
      ],
      [
        <PrivateRoute
          path="/discounts/discountCodes/create"
          component={DiscountCodeCreatePage}
          exact
        />,
        <PrivateRoute path="/discounts/discountCodes/:id" component={DiscountCodeEditPage} exact />,
        <PrivateRoute
          path="/discounts/automaticCodes/create"
          component={AutomaticDiscountCreatePage}
          exact
        />,
        <PrivateRoute
          path="/discounts/automaticCodes/:id"
          component={AutomaticDiscountEditPage}
          exact
        />
      ]
    );
    addPagesRoutes('reviews', [<PrivateRoute path="/reviews" component={Reviews} exact />], []);
    addPagesRoutes(
      'inventory',
      [
        <PrivateRoute path="/inventory" component={Inventory} exact />,
        <PrivateRoute path="/inventory/:id/history" component={InventoryHistory} exact />,
        <PrivateRoute path="/supply" component={Supplies} exact />,
        <PrivateRoute path="/supply/:id/history" component={SupplyHistory} exact />
      ],
      [
        <PrivateRoute path="/inventory/create" component={InventoryCreatePage} exact />,
        <PrivateRoute path="/inventory/:id" component={InventoryEditPage} exact />,
        <PrivateRoute path="/inventory/:id/addStock" component={InventoryAddStockPage} exact />,
        <PrivateRoute path="/supply/create" component={SupplyCreatePage} exact />,
        <PrivateRoute path="/supply/:id" component={SupplyEditPage} exact />,
        <PrivateRoute path="/supply/:id/addStock" component={SupplyAddStockPage} exact />
      ]
    );
    addPagesRoutes(
      'cashupReports',
      [],
      [
        <PrivateRoute path="/cashUp/create" component={CashUpCreatePage} exact />
      ],
      { mutationAction: 'canCreate' }
    );
    addPagesRoutes(
      'cashupReports',
      [
        <PrivateRoute path="/cashUp" component={CashUp} exact />,
        <PrivateRoute path="/cashUp/:id" component={CashUpEditPage} exact />
      ],
      [],
      { viewAction: 'canView' }
    );
    addPagesRoutes('auth', [], []); // TODO: Add auth routes
    addPagesRoutes('company', [], []); // TODO: Add company routes

    routes.push(
      <PrivateRoute path="/routes" component={Routes} exact />,
      <PrivateRoute path="/routes/create" component={RoutesCreatePage} exact />,
      <PrivateRoute path="/routes/:id" component={RoutesEditPage} exact />
    );
    return routes;
  }, [user]);

  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<LoaderOverlay visible />}>
        <Switch>
          <Redirect exact from="/" to="/new-order" />
          {/* ALWAYS ACCESSIBLE PAGES */}
          <PrivateRoute path="/new-order" component={NewOrderNewExperience} exact />
          <PrivateRoute path="/work-board" component={WorkBoard} exact />
          <PrivateRoute path="/cleaning" component={Cleaning} exact />
          <PrivateRoute path="/outsourcing" component={OutsourcingOrdersList} exact />
          <PrivateRoute path="/ready" component={Ready} exact />
          <PrivateRoute path="/pickup" component={Pickup} exact />
          <PrivateRoute path="/Orders" component={Orders} exact />
          <PrivateRoute path="/orders/:id" component={NewOrderNewExperience} exact />
          <PrivateRoute
            path="/orders/:id/print/half-letter"
            component={OrderPrintHalfLetter}
            exact
          />
          {/* <PrivateRoute path="/cashUp" component={CashUp} exact />
          <PrivateRoute path="/cashUp/create" component={CashUpCreatePage} exact />
          <PrivateRoute path="/cashUp/:id" component={CashUpEditPage} exact /> */}
          <PrivateRoute path="/requested-orders" component={RequesedOrders} exact />
          <PrivateRoute path="/reports/financial-summary" component={FinancialSummaryReport} exact />

          {/*routes that need permission access */}
          <PrivateRoute path="/get-more-orders" component={GetMoreOrders} exact />
          {canViewCFDIRoutes && <PrivateRoute path="/CFDI" component={Invoices} exact />}
          {canMutateCFDIRoutes && (
            <PrivateRoute path="/CFDI/preview" component={CFDIPreviewPage} exact />
          )}
          {canViewCFDIRoutes && <PrivateRoute path="/CFDI/orders" component={CFDIOrdersList} exact />}
          <PrivateRoute path="/subscribe" component={Subscribe} exact />
          {...cachedRoutes}
          <PrivateRoute path="/reviews/:storeId/:customerId?" component={CustomerReviews} exact />
          <PrivateRoute path="/profile" component={Profile} exact />
          {/* Rutas públicas accesibles para todos */}
          <Route path="/CFDITermsAndConditions" component={CFDITermsAndConditions} exact />
          <Route path="/terms" component={TermsAndConditions} exact />
          <Route path="/receipt/:publicToken" component={PublicReceipt} exact />
          <Route path="*" component={PageNotFound} exact />
        </Switch>
      </Suspense>
    </ChunkErrorBoundary>
  );
}
